import { ReactNode } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ApiOutlined,
  BarChartOutlined,
  CheckOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  DisconnectOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { SnowflakeMark } from 'features/subscription-freeze/components/freezeUi';
import { Consequence, PlanGroup } from '../billing';

// The building blocks the confirm and pause review steps share, so both read as one dialog.

export type PartTone = 'primary' | 'error' | 'success' | 'warning';

export const CONSEQUENCE_ICONS: Record<Consequence, ReactNode> = {
  accounts: <DisconnectOutlined />,
  dedicatedEnv: <CloudServerOutlined />,
  rooms: <TeamOutlined />,
  // The sidebar's EA Connect icon, so EA hosting looks the same everywhere.
  eaHosting: <ClusterOutlined />,
  aiConnect: <ApiOutlined />,
  analyzer: <BarChartOutlined />
};

/** The pause's mark, sized by the text around it: over its steps and on the buttons that pause. */
export const SNOWFLAKE_ICON = <SnowflakeMark size="1em" strokeWidth={2} />;

/** A tinted note with its icon: the date everything hinges on, or a warning that applies to them. */
export function Callout({ icon, tone, title, children }: { icon: ReactNode; tone: PartTone; title?: string; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={(theme) => ({
        p: 1.75,
        textAlign: 'left',
        borderRadius: RADIUS.tile,
        border: `1px solid ${alpha(theme.palette[tone].main, 0.24)}`,
        bgcolor: alpha(theme.palette[tone].main, 0.05)
      })}
    >
      <Box aria-hidden sx={{ display: 'flex', mt: '2px', color: `${tone}.main`, fontSize: 16 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        {children}
      </Box>
    </Stack>
  );
}

const GROUP_LABEL_SX = {
  display: 'block',
  mb: 0.75,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase'
} as const;

/** A bordered list of titled groups; dividers come from the caller. */
export function GroupList({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ textAlign: 'left', border: '1px solid', borderColor: 'divider', borderRadius: RADIUS.tile, overflow: 'hidden' }}>
      {children}
    </Box>
  );
}

export function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box component="section" sx={{ px: 2, py: 1.5 }}>
      <Typography component="h3" color="text.secondary" sx={GROUP_LABEL_SX}>
        {label}
      </Typography>
      <Stack spacing={1.25}>{children}</Stack>
    </Box>
  );
}

/** One line of a group, marked by a small tile in its tone; `detail` adds a quieter line under it. */
export function IconRow({ icon, tone, label, detail }: { icon: ReactNode; tone: PartTone; label: string; detail?: string }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        aria-hidden
        sx={(theme) => ({
          width: 24,
          height: 24,
          flexShrink: 0,
          borderRadius: '7px',
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          color: `${tone}.main`,
          bgcolor: alpha(theme.palette[tone].main, 0.08)
        })}
      >
        {icon}
      </Box>
      <Box sx={{ pt: '2px', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: detail ? 600 : undefined }}>
          {label}
        </Typography>
        {detail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {detail}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

// A plan reads first: its name, the line under it, any environments, and its end date when dates differ.
export function PlanGroupRow({ group, endsOn }: { group: PlanGroup; endsOn?: string | null }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
          {group.name}
        </Typography>
        {group.detail && (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {group.detail}
          </Typography>
        )}
        {group.items.length > 0 && (
          <Stack component="ul" spacing={0.25} sx={{ mt: 0.5, mb: 0, p: 0, listStyle: 'none' }}>
            {group.items.map((item) => (
              <Stack component="li" key={item} direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                <Box aria-hidden sx={{ display: 'flex', fontSize: 11 }}>
                  <CloudServerOutlined />
                </Box>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
      {endsOn && (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            px: 1,
            py: 0.25,
            borderRadius: RADIUS.pill,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            fontSize: 12,
            fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap'
          }}
        >
          {endsOn}
        </Box>
      )}
    </Stack>
  );
}

// The theme draws checkboxes square; this one is rounded to sit with the panel around it.
const BOX_CLASS = 'confirm-box';
const BOX_SIZE = 18;
const BOX_RADIUS = '5px';

type CheckTone = 'primary' | 'error';

function ConfirmBox({ checked, tone }: { checked: boolean; tone: CheckTone }) {
  return (
    <Box
      className={BOX_CLASS}
      sx={(theme) => ({
        width: BOX_SIZE,
        height: BOX_SIZE,
        borderRadius: BOX_RADIUS,
        display: 'grid',
        placeItems: 'center',
        fontSize: 11,
        color: theme.palette[tone].contrastText,
        border: `1.5px solid ${checked ? theme.palette[tone].main : theme.palette.text.disabled}`,
        bgcolor: checked ? `${tone}.main` : 'background.paper',
        transition: 'background-color .15s ease, border-color .15s ease',
        '@keyframes confirmTick': { from: { opacity: 0, transform: 'scale(.3)' }, to: { opacity: 1, transform: 'scale(1)' } },
        '& .anticon': { animation: 'confirmTick .22s cubic-bezier(.2,.7,.3,1)' },
        [REDUCED_MOTION]: { '& .anticon': { animation: 'none' } }
      })}
    >
      {checked && <CheckOutlined aria-hidden />}
    </Box>
  );
}

type AcknowledgeProps = {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  tone: CheckTone;
};

/** The one tick that unlocks the step's action: they read what it does and say so. */
export function AcknowledgeCheck({ label, checked, onChange, disabled, tone }: AcknowledgeProps) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          color={tone}
          icon={<ConfirmBox checked={false} tone={tone} />}
          checkedIcon={<ConfirmBox checked tone={tone} />}
          sx={(theme) => ({
            p: 0.75,
            borderRadius: '8px',
            '&:hover': { bgcolor: alpha(theme.palette[tone].main, 0.08) },
            [`&:hover .${BOX_CLASS}`]: { borderColor: theme.palette[tone].main }
          })}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={<Typography variant="body2">{label}</Typography>}
      sx={(theme) => ({
        m: 0,
        pl: 0.5,
        pr: 2,
        py: 1,
        textAlign: 'left',
        alignItems: 'flex-start',
        borderRadius: RADIUS.tile,
        border: '1px solid',
        borderColor: checked ? alpha(theme.palette[tone].main, 0.4) : 'divider',
        bgcolor: checked ? alpha(theme.palette[tone].main, 0.04) : 'action.hover',
        transition: 'border-color 150ms, background-color 150ms',
        '& .MuiCheckbox-root': { mt: -0.5 }
      })}
    />
  );
}
